import {
  arrayToSimpleObj,
  getPercentageValue,
  initialUpperCase,
  sortMapAuxiliaryFunc,
  supplementSingleData,
} from '@/pages/opinion/utils';
import { formatDateFunc } from '@/pages/opinion/utils/time';
import { useMemo, useState } from 'react';

// dau数据需要过滤，后期要注意

export function useBatchTypeRequest({
  requestFunc,
  xAxisList,
  dau,
  mentionValue,
}: any) {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [originLineDataMap, setOriginLineDataMap] = useState<{
    [key: string]: Array<any>;
  }>({}); // 折线图所有数据
  const [originBarData, setOriginBarData] = useState<Array<any>>([]); //堆叠图数据men

  const getItemsAndTotalMap = (list: any) => {
    let items = new Set();
    let gameTotalMap: any = {};
    list.forEach(({ value }: any) => {
      let { mentions } = value;
      mentions.forEach((item: { name: any; count: any }) => {
        let key = item.name;
        items.add(key);
        gameTotalMap[key] = (gameTotalMap[key] || 0) + (item.count || 0);
      });
    });

    return { items: Array.from(items), gameTotalMap };
  };

  const getBarLineDataStack = async (params: { date_type: any }) => {
    setIsLoading(true);
    setIsError(false);
    requestFunc({ ...params, top_n: 10 })
      .then((res: any[]) => {
        let data = res || [];
        let { items, gameTotalMap } = getItemsAndTotalMap(data);
        let barMap: { [key: string]: any } = {};
        let lineMap: { [key: string]: any } = {};
        data.map(({ game, value }: any) => {
          const {
            mentions,
            timeline_of_mentions,
            timeline_of_start_time,
            timeline_of_end_time,
          } = value;
          const gameDetail =
            xAxisList?.find(
              (v: { edition_unified_id: any }) =>
                game.edition_unified_id === v.edition_unified_id,
            ) || {};

          // Bar 图表的数据处理
          let mentionsObj = arrayToSimpleObj(mentions, 'count', 'name');
          items.forEach((key: any) => {
            let total = gameTotalMap[key];
            if (!barMap[key]) {
              barMap[key] = {
                name: initialUpperCase(key),
                count: total,
                games: [],
              };
            }
            let value = mentionsObj[key] || 0;
            barMap[key].games.push({
              ...game,
              name: gameDetail.name,
              cover: gameDetail.cover,
              rate: getPercentageValue(value, total, 8),
              value: value,
            });
          });

          // Line 图表数据处理
          // step1: 补充数据
          const list = supplementSingleData(
            timeline_of_start_time,
            timeline_of_end_time,
            timeline_of_mentions,
            params.date_type,
            items as any,
          );

          // step2: 数据分组
          list.forEach((item: { name: any; count: any; time: any }) => {
            // 这个时候name是渠道 或者 地区 或者 语言等
            let key = initialUpperCase(item.name);
            if (!lineMap[key]) {
              lineMap[key] = [];
            }
            // 这里将name重写为游戏名
            lineMap[key].push({
              ...game,
              ...item,
              name: gameDetail.name,
              cover: gameDetail.cover,
              value: item.count || 0,
              time: formatDateFunc(params.date_type)(item.time),
            });
          });
        });
        let barDataList = Object.values(barMap);
        let sorts = xAxisList.map(
          (item: { edition_unified_id: any }) => item.edition_unified_id,
        );
        barDataList.forEach((item) => {
          item.games = item.games.sort(
            (a: { edition_unified_id: any }, b: { edition_unified_id: any }) =>
              sorts.indexOf(a.edition_unified_id) -
              sorts.indexOf(b.edition_unified_id),
          );
        });
        setOriginBarData(barDataList);
        setOriginLineDataMap(lineMap || {});
      })
      .catch(() => {
        setOriginBarData([]);
        setOriginLineDataMap({});
        setIsError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const duaLineDataMap = useMemo(() => {
    let result: any = {};
    for (const key in originLineDataMap) {
      const list = originLineDataMap[key];
      result[key] = list.map((item) => {
        let { time, edition_unified_id } = item;
        let dauValue = dau?.[edition_unified_id]?.[time] || 0;
        return {
          ...item,
          value: getPercentageValue(item.value, dauValue, 18),
        };
      });
    }
    return result;
  }, [dau, originLineDataMap]);

  const duaBarDataMap = useMemo(() => {
    return originBarData.map((item) => ({
      ...item,
      games: item?.games?.map(
        (v: { value?: any; edition_unified_id?: any }) => {
          let { edition_unified_id } = v;
          let dauValue = dau?.[edition_unified_id]?.total || 0;
          return {
            ...v,
            value: getPercentageValue(v.value, dauValue, 18),
          };
        },
      ),
    }));
  }, [dau, originBarData]);

  const lineDataMap = useMemo(() => {
    if (mentionValue === 'mentions') {
      return originLineDataMap;
    }
    // 计算dau数据
    return duaLineDataMap;
  }, [duaLineDataMap, originLineDataMap, mentionValue]);

  const barData = useMemo(() => {
    let data = duaBarDataMap;
    if (mentionValue === 'mentions') {
      data = originBarData;
    }

    const sortMap = data?.reduce(
      (pre, item, index) => ({
        ...pre,
        [item.name]: item.name.toLocaleLowerCase() === 'others' ? 1000 : index,
      }),
      {},
    );
    // 计算dau数据
    return data?.sort(sortMapAuxiliaryFunc(sortMap, (item) => item.name));
  }, [duaBarDataMap, originBarData, mentionValue]);

  return { isLoading, isError, lineDataMap, barData, getBarLineDataStack };
}
